import React, { useState } from "react";
import axios from "axios";
import { Send, User, Mail, Phone, Building, FlaskConical, MessageSquare, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { toast } from "../hooks/use-toast";
import { sustainabilityCategories, scienceImage } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  piName: "",
  institute: "",
  categories: [],
  initiative: "",
};

function validateNomination(form, wordCount) {
  if (!form.firstName || !form.email || !form.mobile || !form.piName || !form.institute) {
    return { title: "Missing details", description: "Please fill all required fields." };
  }
  if (form.categories.length === 0) {
    return { title: "Choose a category", description: "Select at least one sustainability priority." };
  }
  if (wordCount > 300) {
    return { title: "Too long", description: "Please keep your initiative under 300 words." };
  }
  if (form.initiative.trim().length < 10) {
    return { title: "Initiative too short", description: "Please share a bit more detail." };
  }
  return null;
}

function extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ");
  return detail || err.message;
}

const Field = ({ icon: Icon, label, required, children }) => (
  <div>
    <label className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-white/80 mb-2">
      <Icon className="w-3.5 h-3.5" />
      {label}
      {required && <span className="text-[#FFB84A]">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "bg-white/95 border-0 rounded-xl h-12 px-4 text-[#0f2a20] focus-visible:ring-2 focus-visible:ring-[#FFB84A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1F5943]";

export default function RegisterForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCat = (cat) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter((c) => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const wordCount = form.initiative.trim().split(/\s+/).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    const error = validateNomination(form, wordCount);
    if (error) {
      toast(error);
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/nominations`, form);
      setSubmitted(true);
      toast({ title: "Nomination received!", description: "We'll be in touch soon." });
    } catch (err) {
      toast({ title: "Submission failed", description: String(extractErrorMessage(err)) });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm(INITIAL_FORM);
  };

  if (submitted) {
    return (
      <section id="register" className="relative py-24 bg-gradient-to-br from-[#1F5943] to-[#0f2a20] text-white overflow-hidden">
        <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-[#4ADE80]/20 blur-3xl" />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <div className="w-24 h-24 rounded-full bg-[#4ADE80] flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <CheckCircle2 className="w-12 h-12 text-[#0f2a20]" strokeWidth={2.5} />
          </div>
          <h2 className="font-display text-4xl md:text-5xl mb-4">Thank you!</h2>
          <p className="text-white/80 text-lg mb-8">
            Your nomination for <span className="text-[#4ADE80] font-semibold">{form.institute}</span> has been received. Our jury coordinators will reach out with next steps.
          </p>
          <Button
            onClick={resetForm}
            className="bg-white text-[#1F5943] hover:bg-white/90 rounded-full px-8 h-12 font-semibold"
          >
            Submit another nomination
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="register" className="relative py-24 overflow-hidden bg-[#1F5943]">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <img src={scienceImage} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-[#1F5943] via-[#1F5943] to-[#0f2a20] opacity-95 pointer-events-none" />
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-[#4ADE80]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-[#FFB84A]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 text-white sticky top-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 mb-5 backdrop-blur-sm">
              <Send className="w-4 h-4 text-[#FFB84A]" />
              <span className="text-xs font-bold tracking-widest uppercase">Nomination Form</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl mb-5 leading-tight">
              Submit your
              <br />
              <span className="text-[#FFB84A]">nomination.</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              Nominations are open to labs, individuals and organisations
              worldwide. Judging is blind and evidence-based.
            </p>
            <div className="space-y-3">
              {[
                "Free to nominate",
                "Multiple categories allowed",
                "Blind, evidence-based judging",
                "Shortlist announced Sep 2026",
              ].map((x) => (
                <div key={x} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#4ADE80] flex-shrink-0" />
                  <span className="text-white/85">{x}</span>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-8 bg-white/[0.06] backdrop-blur-md rounded-3xl border border-white/15 p-8 md:p-10 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field icon={User} label="First Name" required>
                <Input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="Jane" className={inputCls} />
              </Field>
              <Field icon={User} label="Last Name">
                <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Doe" className={inputCls} />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field icon={Mail} label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@lab.org" className={inputCls} />
              </Field>
              <Field icon={Phone} label="Mobile" required>
                <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 9xxxxxxxxx" className={inputCls} />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field icon={FlaskConical} label="PI Name" required>
                <Input value={form.piName} onChange={(e) => set("piName", e.target.value)} placeholder="Principal Investigator" className={inputCls} />
              </Field>
              <Field icon={Building} label="Institute Name" required>
                <Input value={form.institute} onChange={(e) => set("institute", e.target.value)} placeholder="Your institution" className={inputCls} />
              </Field>
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-white/80 mb-3">
                Sustainability Category <span className="text-[#FFB84A]">*</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                {sustainabilityCategories.map((cat) => {
                  const checked = form.categories.includes(cat);
                  return (
                    <label
                      key={cat}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        checked
                          ? "bg-[#FFB84A]/15 border-[#FFB84A] text-white"
                          : "bg-white/5 border-white/15 text-white/80 hover:bg-white/10"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleCat(cat)}
                        className="border-white/50 data-[state=checked]:bg-[#FFB84A] data-[state=checked]:border-[#FFB84A] data-[state=checked]:text-[#0f2a20]"
                      />
                      <span className="text-sm font-medium">{cat}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <Field icon={MessageSquare} label="Share your initiative (max 300 words)" required>
              <Textarea
                value={form.initiative}
                onChange={(e) => set("initiative", e.target.value)}
                rows={6}
                placeholder="Tell us about your sustainability initiative, measurable impact, and roadmap forward..."
                className="bg-white/95 border-0 rounded-xl p-4 text-[#0f2a20] focus-visible:ring-2 focus-visible:ring-[#FFB84A] resize-none"
              />
              <div className={`text-xs mt-2 text-right ${wordCount > 300 ? "text-[#FCA5A5]" : "text-white/60"}`}>
                {wordCount} / 300 words
              </div>
            </Field>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-xs text-white/50 max-w-md">
                By submitting, you consent to being contacted about your
                nomination. Data is handled per our privacy policy.
              </p>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#FFB84A] hover:bg-[#F59E0B] text-[#0f2a20] rounded-full px-10 h-14 text-base font-bold shadow-lg hover:shadow-xl transition-all w-full sm:w-auto disabled:opacity-70"
              >
                {loading ? (
                  <>
                    Submitting
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  </>
                ) : (
                  <>
                    Submit Nomination
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
